import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { Flex } from 'theme-ui'
import { withAlertContextProvider, AlertPresenter } from 'ui'
import { useLocation } from '@reach/router'
import OrgStaticPage from 'components/Organization/HomePage/OrgStaticPage'
import { orgStaticPageHeaderHeight } from 'components/NewStaticPage/StaticPageHeader'
import Hero from 'components/FreeDns/sections/Hero'
import NeedMoreControl from 'components/FreeDns/sections/NeedMoreControl'
import StandardConfigurations from 'components/FreeDNSPage/StandardConfigurations'
import ThirdPartyFilters from 'components/FreeDns/sections/ThirdPartyFilters/ThirdPartyFilters'
import CustomConfiguration from 'components/FreeDns/sections/CustomConfiguration'
import FreeDNSTray from 'components/FreeDNSPage/FreeDNSTray/FreeDNSTray'
import FreeDNSTrayContent from 'components/FreeDNSPage/FreeDNSTray/FreeDNSTrayContent'
import useStepHelper from 'components/SetupGuide/useStepHelper'
import { useAppSelector } from 'store/hooks'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import useFocusLock from 'utils/useFocusLock'
import useQueryString from 'utils/useQueryString'
import GetStartedHelpPane from 'components/SetupGuide/SetupGuide'
import FreeDNSFeaturesCards from 'components/FreeDns/sections/FreeDNSFeaturesCards'
import { frequentlyAskedQuestions } from './sections/frequentlyAskedQuestionsData'
import FrequentlyAskedQuestions from 'components/FrequentlyAskedQuestions'

export const freeDnsPagePath = 'free-dns'

export default withAlertContextProvider(function OrgHomePage(): ReactElement {
  const alertPresenterContainerRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const [isOpenTray, setIsOpenTray] = useState(false)
  const { qs } = useQueryString()
  const { closePane } = useStepHelper()
  const {
    selectedFiltersPKs = [],
    selectedDNSKey,
    selected3rdPartyFilterPK,
  } = useAppSelector(s => s.freeDNS)
  const isMobile = useBreakpointIndex() === 0

  useFocusLock(isOpenTray)

  // document body scroll should be hidden when tray is opened
  useEffect(() => {
    if (qs.tray || (qs.helpPane && (isOpenTray || isMobile))) {
      document.body.style.overflowY = 'hidden'
      return
    }

    document.body.style.overflowY = 'auto'
  }, [isOpenTray, isMobile, qs])

  // should close helpPane on first render
  useEffect(() => {
    closePane()
    // we only want to trigger this effect when component is first rendered, not when
    // `closePane` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setIsOpenTray(
      !!(selectedFiltersPKs?.length || (!isMobile && (selected3rdPartyFilterPK || selectedDNSKey))),
    )
  }, [selectedFiltersPKs.length, selectedDNSKey, selected3rdPartyFilterPK, isMobile])

  useEffect(() => {
    // fix for the page position issue on iOS 14
    !location.hash && (document.body.scrollTop = document.documentElement.scrollTop = 0)
  }, [location.hash])

  return (
    <OrgStaticPage
      hasMaxWidth={true}
      intraPageLinks={[{ text: 'Pricing', to: '/pricing' }]}
      customTrayOrModalDialog={
        <>
          <FreeDNSTray
            isOpen={isOpenTray}
            showOverlay={false}
            entersFrom={'bottom'}
            slideDimensionLength={'auto'}
            zIndexName="zIndex9"
            sxTray={{
              height: 'fit-content',
              backgroundColor: 'blue800',
              borderTopLeftRadius: '1.2rem',
              borderTopRightRadius: '1.2rem',
            }}
          >
            <FreeDNSTrayContent setIsOpenTray={setIsOpenTray} />
          </FreeDNSTray>
          <GetStartedHelpPane />
        </>
      }
    >
      <Flex
        className="ios-scrolling-glitches-fix"
        sx={{
          width: '100%',
          height: '100%',
          flexDirection: 'column',
          alignItems: 'center',
          '& [id]': {
            scrollMarginTop: orgStaticPageHeaderHeight,
          },
        }}
      >
        <Hero />
        <StandardConfigurations />
        <FreeDNSFeaturesCards />
        {/* Needs to show bottom panel and tray/dialog. Replace them with the updated components. */}
        <ThirdPartyFilters />
        {/* remove above ^ */}
        <CustomConfiguration />
        <NeedMoreControl />
        <FrequentlyAskedQuestions
          data={frequentlyAskedQuestions}
          sx={{
            my: '10rem',
          }}
        />
      </Flex>
      <Flex
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          position: 'fixed',
          justifyContent: 'center',
          zIndex: 'zIndex650',
          pointerEvents: 'none',
        }}
      >
        <Flex
          ref={alertPresenterContainerRef}
          sx={{
            width: '100%',
            maxWidth: ['100%', '100%', '108rem'],
            mx: ['1.6rem', '1.6rem', '4.8rem'],
            [`@media screen and (min-width: 1120px) and (max-width: 1450px)`]: {
              mx: '2.4rem',
            },
          }}
        >
          <AlertPresenter containerRef={alertPresenterContainerRef} />
        </Flex>
      </Flex>
    </OrgStaticPage>
  )
})
